import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import PageStructure from 'flarum/forum/components/PageStructure';
import BlogOverview from 'ext:fof/blog/forum/pages/BlogOverview';
import PageHero from 'ext:fof/pages/forum/components/PageHero';
const COLLAPSE_KEY = 'headerImageCollapsed';

/* =========================================================
   Seiten mit zusätzlichem Hero
   ========================================================= */
const HERO_PAGES = {
  '/': {
    title: 'Startseite',
    icon: 'fas fa-comments'
  },
  '/categories': {
    title: 'Foren-Übersicht',
    icon: 'fas fa-list-ul'
  },
  '/blog': {
    title: 'Blog',
    icon: 'fas fa-blog'
  },
  '/calendar': {
    title: 'Kalender',
    icon: 'far fa-calendar-days'
  },
  '/bookmarks': {
    title: 'Lesezeichen',
    icon: 'fas fa-bookmark'
  },
  '/bookmarked-posts': {
    title: 'Lesezeichen',
    icon: 'fas fa-bookmark'
  },
  '/p/25-kartenlegen': {
    title: 'Kartenlegen',
    icon: 'fas fa-diamond'
  },
  '/p/31-tageskarte': {
    title: 'Tageskarte',
    icon: 'fas fa-sun'
  }
};

/* =========================================================
   Hero-Daten für aktuelle Seite
   ========================================================= */
function getHeroData() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (HERO_PAGES[path]) {
    return HERO_PAGES[path];
  }
  return null;
}

/* =========================================================
   Header Image + Shell erzeugen
   ========================================================= */
function ensureHeaderImage() {
  let shell = document.querySelector('.HeaderImage-shell');
  if (shell) {
    const headerImage = shell.querySelector('.HeaderImage');
    if (headerImage) {
      return {
        shell,
        headerImage
      };
    }
  }
  const quickbar = document.querySelector('.qs-bar');
  if (!quickbar) {
    return null;
  }
  shell = document.createElement('div');
  shell.className = 'HeaderImage-shell';
  const headerImage = document.createElement('div');
  headerImage.className = 'HeaderImage';
  shell.appendChild(headerImage);
  quickbar.insertAdjacentElement('afterend', shell);
  return {
    shell,
    headerImage
  };
}

/* =========================================================
   Einklapp-Button
   ========================================================= */
function bindCollapse(shell) {
  if (!shell || shell.dataset.collapseBound === '1') {
    return;
  }
  shell.dataset.collapseBound = '1';
  let toggle = shell.querySelector('.HeaderImage-toggle');
  if (!toggle) {
    toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'HeaderImage-toggle';
    toggle.setAttribute('aria-label', 'Headerbild ein-/ausklappen');
    toggle.innerHTML = `
      <svg viewBox="0 0 10 10" aria-hidden="true">
        <path
          d="M1 3 l4 4 4-4"
          fill="none"
          stroke="white"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
    shell.appendChild(toggle);
  }
  let collapsed = false;
  try {
    collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch (e) {}
  shell.classList.toggle('is-collapsed', collapsed);
  toggle.addEventListener('click', e => {
    e.preventDefault();
    e.stopPropagation();
    const newState = !shell.classList.contains('is-collapsed');
    shell.classList.toggle('is-collapsed', newState);
    try {
      localStorage.setItem(COLLAPSE_KEY, newState ? '1' : '0');
    } catch (e) {}
  });
}

/* =========================================================
   Header Image rendern
   ========================================================= */
function renderHeaderImage() {
  const elements = ensureHeaderImage();
  if (!elements) {
    return;
  }
  const {
    shell,
    headerImage
  } = elements;
  const lightImageUrl = app.forum.attribute('headerImageUrl');
  const darkImageUrl = app.forum.attribute('headerImageDarkUrl');
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const imageUrl = isDark && darkImageUrl ? darkImageUrl : lightImageUrl;
  if (!imageUrl) {
    shell.style.display = 'none';
    return;
  }
  shell.style.display = '';
  headerImage.style.backgroundImage = `url("${imageUrl}")`;
  let content = headerImage.querySelector('.HeaderImage-content');
  if (!content) {
    content = document.createElement('div');
    content.className = 'HeaderImage-content';
    content.innerHTML = `
      <div class="HeaderImage-title">
        e s o ℕ e t z
      </div>
      <div class="HeaderImage-tagline">
        Bewusstsein, Entfaltung &amp; Esoterik
      </div>
    `;
    headerImage.appendChild(content);
  }
  bindCollapse(shell);
}

/* =========================================================
   Extension
   ========================================================= */
app.initializers.add('kojonos-header-image', () => {
  /* Headerbild wie bisher */
  app.beforeMount(() => {
    renderHeaderImage();
    const themeObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          renderHeaderImage();
          break;
        }
      }
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  });

  /* =========================================================
     Custom Hero direkt über Flarum PageStructure
     ========================================================= */
  extend(PageStructure.prototype, 'mainItems', function (returned) {
    const data = getHeroData();
    if (!data) return;
    returned.setContent('hero', m("div", {
      className: "Page-hero"
    }, m("header", {
      className: "Hero TagHero CustomFlarumHero"
    }, m("div", {
      className: "container",
      style: {
        position: 'relative'
      }
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      "aria-hidden": "true",
      className: 'icon ' + data.icon + ' text-colored'
    }), ' ', m("span", {
      className: "CustomFlarumHero-title"
    }, data.title)), data.subtitle ? m("div", {
      className: "Hero-subtitle"
    }, data.subtitle) : null)))));
  });

  /* =========================================================
     Hero für FoF Pages
     ========================================================= */
  override(PageHero.prototype, 'view', function (original) {
    const page = this.attrs.page;
    if (!page) {
      return original();
    }
    const path = window.location.pathname.replace(/\/+$/, '') || '/';

    /*
     * Individuelles Icon aus HERO_PAGES verwenden.
     * Für alle anderen FoF Pages Standard-Icon.
     */
    const configured = HERO_PAGES[path];
    const icon = configured?.icon || 'fas fa-file-lines';

    /*
     * Den echten Titel direkt aus FoF Pages holen.
     */
    const title = configured?.title || page.attribute('title') || 'Seite';
    return m("header", {
      className: "Hero TagHero CustomFlarumHero"
    }, m("div", {
      className: "container",
      style: {
        position: 'relative'
      }
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      "aria-hidden": "true",
      className: 'icon ' + icon + ' text-colored'
    }), ' ', m("span", {
      className: "CustomFlarumHero-title"
    }, title)))));
  });

  /* =========================================================
     Hero für FoF Blog
     ========================================================= */
  override(BlogOverview.prototype, 'hero', function (original) {
    const data = getHeroData();
    if (!data) {
      return original();
    }
    return m("header", {
      className: "Hero TagHero CustomFlarumHero"
    }, m("div", {
      className: "container",
      style: {
        position: 'relative'
      }
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      "aria-hidden": "true",
      className: 'icon ' + data.icon + ' text-colored'
    }), ' ', m("span", {
      className: "CustomFlarumHero-title"
    }, data.title)), data.subtitle ? m("div", {
      className: "Hero-subtitle"
    }, data.subtitle) : null)));
  });

  /* =========================================================
    Hero für Kalender
    ========================================================= */
  app.beforeMount(() => {
    const CalendarPage = app.routes.calendar?.component;
    if (!CalendarPage?.prototype) {
      return;
    }
    override(CalendarPage.prototype, 'view', function (original) {
      const content = original();
      const data = getHeroData();
      if (!data) {
        return content;
      }
      return [m("header", {
        className: "Hero TagHero CustomFlarumHero"
      }, m("div", {
        className: "container",
        style: {
          position: 'relative'
        }
      }, m("div", {
        className: "containerNarrow"
      }, m("h1", {
        className: "Hero-title"
      }, m("i", {
        "aria-hidden": "true",
        className: 'icon ' + data.icon + ' text-colored'
      }), ' ', m("span", {
        className: "CustomFlarumHero-title"
      }, data.title)), data.subtitle ? m("div", {
        className: "Hero-subtitle"
      }, data.subtitle) : null))), content];
    });
  });
});